import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/features/auth/stores/auth';
import { confirmDiscardUnsavedDialogChanges } from '@/lib/unsaved-dialog-guard';
import { menuKeyFromPath } from '@/config/menus';
import { isTenantMenuAllowed } from './phase1MenuAccess';
import { authRoutes } from './auth.routes';
import { billingRoutes } from './billing.routes';
import { operatorRoutes } from './operator.routes';
import { tenantLayoutRoute } from './tenant.routes';
import { webhookRoutes } from './webhook.routes';
import {
  cancelRoutePending,
  installRouteNavigationState,
} from '@/shared/composables/useRouteNavigationState';
import { configureApiNavigation } from '@/shared/api/client';

const routes: RouteRecordRaw[] = [
  ...authRoutes,
  ...operatorRoutes,
  {
    ...tenantLayoutRoute,
    children: [...(tenantLayoutRoute.children ?? []), ...webhookRoutes, ...billingRoutes],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

configureApiNavigation({
  getCurrentRouteName: () => router.currentRoute.value.name,
  getCurrentFullPath: () => router.currentRoute.value.fullPath,
  push: (to) => router.push(to),
});

installRouteNavigationState(router);

router.beforeEach(async (to, from, next) => {
  if (to.fullPath !== from.fullPath) {
    const canLeave = await confirmDiscardUnsavedDialogChanges();
    if (!canLeave) {
      cancelRoutePending();
      next(false);
      return;
    }
  }

  const authStore = useAuthStore();
  await authStore.ensureAuthChecked();
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false);

  if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } });
    return;
  }

  if (to.matched.some((record) => record.meta.requiresOperator)) {
    if (!authStore.user?.isPlatformOperator || authStore.isImpersonating) {
      next({ name: 'Dashboard' });
      return;
    }
  }

  const menuKey =
    (to.meta.menuKey as string | undefined) || menuKeyFromPath(to.path) || undefined;
  const skipTenantMenuCheck = Boolean(
    authStore.user?.isPlatformOperator && !authStore.isImpersonating,
  );
  if (
    authStore.isAuthenticated &&
    !isTenantMenuAllowed(menuKey, {
      skipTenantMenuCheck,
      canAccessMenu: (key) => authStore.canAccessMenu(key),
    })
  ) {
    next({ name: 'Forbidden' });
    return;
  }

  if (to.name === 'Login' && authStore.isAuthenticated) {
    const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : '';
    if (redirect.startsWith('/') && !redirect.startsWith('//') && redirect !== '/login') {
      next(redirect);
    } else {
      next({ name: 'Dashboard' });
    }
    return;
  }

  if (
    (to.name === 'PersonalRegister' || to.name === 'VerifyEmail') &&
    authStore.isAuthenticated
  ) {
    next({ name: 'Dashboard' });
    return;
  }

  next();
});

export default router;
